// src/components/organism/ResetPassword/RecoveryVerifyEmailOtpForm.tsx
import React from 'react';
import { Card, Col, Container, Row, Form, TextIconButton, ArrowLeftIcon } from 'shared-portal-component';
import { z } from 'zod';

import AuthBodyText from '@/components/atoms/AuthBodyText/AuthBodyText';
import AuthHeaderText from '@/components/atoms/AuthHeaderText/AuthHeaderText';
import { MIN_LEN } from '@/constants';

const CODE_LENGTH = 6;

const schema = z.object({
  code: z
    .string()
    .length(CODE_LENGTH, `Code must be exactly ${CODE_LENGTH} digits`)
    .regex(/^\d+$/, 'Code must contain only digits'),
});

interface RecoveryVerifyEmailOtpFormProps {
  error?: string;
  onSubmit: (code: string) => void | Promise<void>;
}

export default function RecoveryVerifyEmailOtpForm({
  error,
  onSubmit,
}: RecoveryVerifyEmailOtpFormProps): JSX.Element {
  const fields = React.useMemo(
    () => [
      {
        name: 'code',
        label: 'Code',
        placeholder: 'Enter the 6-digit code',
        required: true,
        variant: 'outline' as const,
        type: 'code',
        numValues: CODE_LENGTH,
      },
    ],
    []
  );

  return (
    <Container fluid>
      <Card>
        <Row spacing="md">
          <Col size={12}>
            <AuthHeaderText text="Verify your email" />
            <AuthBodyText>
              We’ve sent a 6-digit code to your email address. Please enter it below.
            </AuthBodyText>

            {typeof error === 'string' && error.trim().length > MIN_LEN && (
              <div className="text-destructive mt-2">{error}</div>
            )}
          </Col>
        </Row>

        <Row justify="center" spacing="none">
          <Form
            schema={schema}
            fields={fields}
            onSubmit={(values: { code: string }) => {
              void onSubmit(values.code);
            }}
          />
        </Row>
      </Card>
    </Container>
  );
}
